import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useCalendarReminderStore, ActiveCalendarReminder } from '../store/calendarReminderStore';
import { useThemedStyles } from '../hooks/useThemedStyles';

const formatEventTime = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMinutesBefore = (minutes?: number): string | null => {
  if (minutes == null) return null;
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return `Starts in ${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `Starts in ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

interface CalendarReminderAlertScreenProps {
  reminder: ActiveCalendarReminder;
}

/**
 * Full-screen, alarm-clock-style takeover for a fired calendar reminder —
 * genuinely new UI pattern in this app (every existing Modal is a small
 * transient dialog; PhoneCallScreen is an embedded panel, not a takeover).
 * Mounted via CalendarReminderAlertRoot at the App.tsx root so it renders
 * above ALL navigation regardless of currentScreen. Deliberately minimal
 * state machine — triggered → dismissed | viewed — matching "alarm alert":
 * full-screen and dismissible, no snooze.
 */
export const CalendarReminderAlertScreen: React.FC<CalendarReminderAlertScreenProps> = ({ reminder }) => {
  const styles = useThemedStyles(getStyles);
  const dismissReminder = useCalendarReminderStore((state) => state.dismissReminder);
  const requestNavigation = useCalendarReminderStore((state) => state.requestNavigation);

  const handleDismiss = () => {
    dismissReminder();
  };

  const handleViewEvent = () => {
    requestNavigation('event-detail', reminder.calendarEventId);
    dismissReminder();
  };

  const subtitle = formatMinutesBefore(reminder.minutesBefore);

  return (
    <View style={styles.container}>
      <View style={styles.iconWrap}>
        <Ionicons name="alarm" size={64} color="#FFFFFF" />
      </View>

      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
      <Text style={styles.title} numberOfLines={3}>{reminder.title}</Text>
      <Text style={styles.time}>{formatEventTime(reminder.startDate)}</Text>
      {!!reminder.location && <Text style={styles.location}>{reminder.location}</Text>}

      <View style={styles.actions}>
        <TouchableOpacity style={styles.dismissButton} onPress={handleDismiss} activeOpacity={0.8}>
          <Text style={styles.dismissButtonText}>Dismiss</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.viewButton} onPress={handleViewEvent} activeOpacity={0.85}>
          <Text style={styles.viewButtonText}>View Event</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
    ...(Platform.OS === 'web' ? { position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0 } : {}),
  },
  iconWrap: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xl,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: 'rgba(255,255,255,0.85)',
    fontWeight: '600' as const,
    marginBottom: spacing.sm,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    color: '#FFFFFF',
    fontWeight: 'bold' as const,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  time: {
    fontSize: typography.fontSize.lg,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  location: {
    fontSize: typography.fontSize.base,
    color: 'rgba(255,255,255,0.75)',
    textAlign: 'center',
  },
  actions: {
    position: 'absolute',
    bottom: spacing.xl * 2,
    left: spacing.xl,
    right: spacing.xl,
    flexDirection: 'row',
    gap: spacing.md,
  },
  dismissButton: {
    flex: 1,
    paddingVertical: spacing.md,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dismissButtonText: {
    color: '#FFFFFF',
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
  },
  viewButton: {
    flex: 1,
    paddingVertical: spacing.md,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewButtonText: {
    color: colors.primary as string,
    fontSize: typography.fontSize.base,
    fontWeight: 'bold' as const,
  },
});
